import React, { useState, useCallback, useEffect } from "react";
import { Box, Dialog, IconButton, Fade } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, Zoom } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/zoom";

import { ImageGalleryLightboxProps } from "./ImageGalleryTypes";
import {
  lightboxSx,
  lightboxToolbarSx,
  lightboxCounterSx,
} from "./galleryStyles";
import { dt } from "../../themes/redesignTokens";

const ImageGalleryLightbox: React.FC<ImageGalleryLightboxProps> = ({
  images,
  open,
  onClose,
  initialIndex = 0,
  showDownload = true,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const hasMultiple = images.length > 1;

  // Sync initialIndex when lightbox opens
  useEffect(() => {
    if (open && swiperInstance) {
      swiperInstance.slideTo(initialIndex, 0);
      setActiveIndex(initialIndex);
      setIsZoomed(false);
    }
  }, [open, initialIndex, swiperInstance]);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex);
    setIsZoomed(false);
  }, []);

  // Track zoom state via Swiper's zoomChange event
  const handleZoomChange = useCallback(
    (_swiper: SwiperType, scale: number) => {
      setIsZoomed(scale > 1);
    },
    []
  );

  // Single-click toggles zoom
  const handleImageClick = useCallback(() => {
    if (swiperInstance?.zoom) {
      swiperInstance.zoom.toggle();
    }
  }, [swiperInstance]);

  const handleDownload = useCallback(() => {
    const image = images[activeIndex];
    if (!image) return;

    const link = document.createElement("a");
    link.href = image.src;
    link.download = image.name || `image-${activeIndex + 1}`;

    // For base64 data URLs AND blob: URLs (same-origin object URLs produced
    // by `URL.createObjectURL`), the `download` attribute works directly and
    // triggers a real browser download without opening a new tab.
    if (image.src.startsWith("data:") || image.src.startsWith("blob:")) {
      link.click();
    } else {
      // Open in new tab for cross-origin URLs (browser handles download)
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    }
  }, [images, activeIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  if (!images.length) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={lightboxSx}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      onKeyDown={handleKeyDown}
    >
      {/* Top toolbar: download + close */}
      <Box sx={lightboxToolbarSx}>
        {showDownload && (
          <IconButton
            onClick={handleDownload}
            aria-label="Download image"
            sx={{
              color: "#FFFFFF",
              backgroundColor: "rgba(255,255,255,0.1)",
              width: "40px",
              height: "40px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <DownloadOutlinedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
        <IconButton
          onClick={onClose}
          aria-label="Close gallery"
          sx={{
            color: "#FFFFFF",
            backgroundColor: "rgba(255,255,255,0.1)",
            width: "40px",
            height: "40px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.2)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      {/* Swiper with zoom */}
      <Swiper
        modules={[Navigation, Keyboard, Zoom]}
        navigation={hasMultiple}
        keyboard={{ enabled: true }}
        zoom={{ maxRatio: 3, minRatio: 1 }}
        initialSlide={initialIndex}
        onSlideChange={handleSlideChange}
        onZoomChange={handleZoomChange}
        onSwiper={setSwiperInstance}
        loop={false}
        spaceBetween={0}
        slidesPerView={1}
        style={{
          width: "100%",
          height: "100%",
          cursor: isZoomed ? "zoom-out" : "zoom-in",
        }}
      >
        {images.map((image, index) => (
          <SwiperSlide key={image.id}>
            <div
              className="swiper-zoom-container"
              style={{ cursor: isZoomed ? "zoom-out" : "zoom-in" }}
            >
              <img
                src={image.src}
                alt={image.alt || `Image ${index + 1}`}
                draggable={false}
                loading="lazy"
                onClick={handleImageClick}
                style={{ cursor: isZoomed ? "zoom-out" : "zoom-in" }}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Counter at bottom center */}
      {hasMultiple && (
        <Box sx={lightboxCounterSx}>
          <Box
            component="span"
            sx={{ fontFamily: dt.fontMono }}
          >
            {activeIndex + 1}
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: dt.font,
              mx: "4px",
              opacity: 0.6,
              fontSize: "12px",
            }}
          >
            /
          </Box>
          <Box
            component="span"
            sx={{ fontFamily: dt.fontMono }}
          >
            {images.length}
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default ImageGalleryLightbox;
